import React, { useState, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Avatar as MuiAvatar,
  Chip,
  Menu,
  MenuItem
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import FlagIcon from '@material-ui/icons/Flag'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import PinIcon from '@material-ui/icons/PushPin'
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined'
import CommentOutlinedIcon from '@material-ui/icons/CommentOutlined'
import EditIcon from '@material-ui/icons/Edit'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import DeleteIcon from '@material-ui/icons/Delete'
import Avatar from 'components/Avatar'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { formatCreatedAt } from 'utils/times'
import { getInitials } from 'utils/getInitials'
import ConfirmationModal from 'components/ConfirmationModal'
import { useConfirm } from 'material-ui-confirm'
import CreatePostModal from './CreatePostModal'
import { toast } from 'utils/notify'
import {
  deleteCommunityPost,
  pinOrUnpinPost,
  sendPinOrUnpinPostNotification
} from 'store/community'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import GetAppIcon from '@material-ui/icons/GetApp'
import { storageRef } from 'index'

const cardTypes = Object.freeze({
  compressed: 'compressed',
  expanded: 'expanded'
})

const PostCard = ({ cardType = cardTypes.compressed, post, conferenceId, postTopics }) => {
  const history = useHistory()
  const classes = useStyles()
  const confirm = useConfirm()

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [performingAction, setPerformingAction] = useState(false)
  const [attachmentUrl, setAttachmentUrl] = useState(null)
  const [attachmentLoading, setAttachmentLoading] = useState(false)

  const attachment = post?.attachments?.[0] || null

  useEffect(() => {
    let cancelled = false

    const loadAttachmentUrl = async () => {
      if (attachment?.path) {
        try {
          setAttachmentLoading(true)
          const relativePath = attachment.path.replace('images/', '')
          const url = await storageRef.child(relativePath).getDownloadURL()
          if (!cancelled) setAttachmentUrl(url)
        } catch (err) {
          console.error('Failed to load attachment:', err)
        } finally {
          if (!cancelled) setAttachmentLoading(false)
        }
      }
    }
    loadAttachmentUrl()

    return () => {
      cancelled = true
    }
  }, [attachment?.path])

  // New: handle card click to open full post view
  const handleCardClick = () => {
    history.push(`/admin/community-posts/${post.id}`)
  }

  const handleOpenMenu = (event) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleMenuAction = (action) => {
    handleCloseMenu()
    switch (action) {
      case 'view':
        handleCardClick()
        break
      case 'edit':
        handleEditPost(post)
        break
      case 'delete':
        handleDeleteClick()
        break
      case 'pin':
        handleTogglePin(post)
        break
      default:
        break
    }
  }

  // Open post editor modal
  const handleEditPost = () => setEditModalOpen(true)
  // Show delete confirmation
  const handleDeleteClick = () => setDeleteModalOpen(true)

  // Delete post
  const handleDeleteConfirm = async () => {
    try {
      setPerformingAction(true)
      if (post?.id && conferenceId) {
        await deleteCommunityPost(post?.id)
        toast.success('Post deleted.')
        if (cardType === cardTypes.expanded) {
          goBack()
        }
      }
    } catch (err) {
      toast.error(`Failed to delete post: ${err.message}`)
    } finally {
      setDeleteModalOpen(false)
      setPerformingAction(false)
    }
  }

  // Toggle pin post
  const handleTogglePin = async (post) => {
    try {
      await confirm({
        title: post.isPinned ? 'Unpin Post' : 'Pin Post',
        description: post.isPinned
          ? 'Are you sure you want to unpin this post?'
          : 'Are you sure you want to pin this post?',
        confirmationText: 'Yes',
        cancellationText: 'No'
      })

      await pinOrUnpinPost(post?.id, {
        isPinned: !post.isPinned
      })
      await sendPinOrUnpinPostNotification(post?.id)
    } catch (error) {
      // User cancelled the action
    }
  }
  const goBack = () => history.goBack()

  return (
    <Paper elevation={1} className={classes.postCard}>
      <Box className={classes.postCardContent}>
        {/* Header with author info and post metadata */}
        <Box className={classes.authorSection}>
          {cardType !== cardTypes.compressed && (
            <IconButton size="small" onClick={goBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          {conferenceId && post?.author?.profilePicture ? (
            <Avatar
              conferenceId={conferenceId}
              imageName={post?.author?.profilePicture}
              path="attendees"
              height={24}
              width={24}
              progressSize={16}
              className={classes.avatarImage}
            />
          ) : (
            <MuiAvatar className={classes.avatar}>{getInitials(post?.author?.name)}</MuiAvatar>
          )}
          <Box className={classes.authorInfo}>
            <Box className={classes.authorNameTime}>
              <Typography variant="subtitle2" className={classes.authorName}>
                {post?.author?.name || 'Anonymous'}
              </Typography>
              <span className={classes.metadataDot}>•</span>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.postTime}
                title={moment(post?.createdAt).format('MMM DD, YYYY hh:mm A')}>
                {formatCreatedAt(post?.createdAt)}
              </Typography>
            </Box>
          </Box>

          {/* Post actions */}
          <Box className={classes.actionButtons}>
            <IconButton size="small" onClick={handleOpenMenu} aria-label="post actions">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {/* Post content */}
        <Box
          className={classes.postSection}
          style={cardType === cardTypes.compressed ? { cursor: 'pointer' } : {}}
          onClick={() => (cardType === cardTypes.compressed ? handleCardClick() : null)}>
          <Typography variant="h6" className={classes.postTitle}>
            {post?.title}
            {(post?.isPinned || post?.isFlagged) && (
              <Box component="span" className={classes.statusIndicators}>
                {post?.isPinned && (
                  <Box component="span" className={`${classes.statusItem} ${classes.pinItem}`}>
                    <PinIcon fontSize="small" className={classes.pinIconInline} />
                    <Typography variant="caption" className={classes.statusLabel}>
                      Pinned
                    </Typography>
                  </Box>
                )}
                {post?.isFlagged && (
                  <Box component="span" className={`${classes.statusItem} ${classes.flagItem}`}>
                    <FlagIcon fontSize="small" className={classes.flagIconInline} />
                    <Typography variant="caption" className={classes.statusLabel}>
                      Flagged
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Typography>

          {cardType === cardTypes.expanded && (
            <Box className={classes.postMeta} style={{ margin: '12px 0px 10px 0px' }}>
              {post?.topics && post?.topics.length > 0 && (
                <>
                  {post?.topics.map((topic, index) => (
                    <Chip
                      key={index}
                      label={topic?.title || '[Deleted]'}
                      size="small"
                      className={classes.categoryChip}
                    />
                  ))}
                </>
              )}
            </Box>
          )}

          <Typography
            variant="body2"
            className={classes.postContent}
            title={post?.content || 'N/A'}>
            {cardType === cardTypes.compressed
              ? post?.content && post?.content.length > 300
                ? `${post?.content.substring(0, 300)}...`
                : post?.content
              : post?.content}
          </Typography>

          {/* Attachment display */}
          {attachment && (
            <Box className={classes.attachmentContainer}>
              {attachmentLoading ? (
                <CircularProgress size={24} />
              ) : attachment.type?.startsWith('image/') ? (
                <Box
                  className={
                    cardType === cardTypes.compressed
                      ? classes.attachmentImageCompressed
                      : classes.attachmentImageExpanded
                  }>
                  {attachmentUrl && (
                    <img
                      src={attachmentUrl}
                      alt="Post attachment"
                      className={classes.attachmentImg}
                    />
                  )}
                </Box>
              ) : (
                <Box
                  className={classes.pdfAttachment}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (attachmentUrl) window.open(attachmentUrl, '_blank')
                  }}
                  style={{ cursor: 'pointer' }}>
                  <PictureAsPdfIcon color="error" style={{ fontSize: 32 }} />
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {attachment.filename}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      PDF Document - Click to download
                    </Typography>
                  </Box>
                  <GetAppIcon fontSize="small" color="primary" style={{ marginLeft: 'auto' }} />
                </Box>
              )}
            </Box>
          )}

          {/* Post stats */}
          <Box className={classes.postStats}>
            <Box className={classes.statsWrapper}>
              <Box className={classes.statItem}>
                <ThumbUpOutlinedIcon fontSize="small" />
                <Typography variant="body2" className={classes.statCount}>
                  {post?.likes || 0} {post?.likes === 1 ? 'Like' : 'Likes'}
                </Typography>
              </Box>
              <Box className={classes.statItem}>
                <CommentOutlinedIcon fontSize="small" />
                <Typography variant="body2" className={classes.statCount}>
                  {post?.noOfComments || 0} {post?.noOfComments === 1 ? 'Comment' : 'Comments'}
                </Typography>
              </Box>
              {cardType === cardTypes.compressed && (
                <Box className={classes.postMeta}>
                  {post?.topics && post?.topics.length > 0 && (
                    <>
                      {post?.topics.slice(0, 2).map((topic, index) => (
                        <Chip
                          key={index}
                          label={topic?.title || '[Deleted]'}
                          size="small"
                          className={classes.categoryChip}
                        />
                      ))}
                      {post?.topics.length > 2 && (
                        <Chip
                          label={`+${post?.topics.length - 2} more`}
                          size="small"
                          className={classes.moreChip}
                        />
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Popup menu */}
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClick={(e) => e.stopPropagation()}
          onClose={handleCloseMenu}>
          {cardType === cardTypes.compressed && (
            <MenuItem onClick={() => handleMenuAction('view')}>
              <VisibilityOutlinedIcon fontSize="small" color="primary" />
              <Typography style={{ marginLeft: 8 }}>View Post</Typography>
            </MenuItem>
          )}
          <MenuItem onClick={() => handleMenuAction('edit')}>
            <EditIcon fontSize="small" color="primary" />
            <Typography style={{ marginLeft: 8 }}>Edit Post</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction('pin')}>
            <PinIcon fontSize="small" color="primary" />
            <Typography style={{ marginLeft: 8 }}>
              {post?.isPinned ? 'Unpin Post' : 'Pin Post'}
            </Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction('delete')}>
            <DeleteIcon fontSize="small" color="error" />
            <Typography style={{ marginLeft: 8 }}>Delete Post</Typography>
          </MenuItem>
        </Menu>
      </Box>

      <ConfirmationModal
        open={deleteModalOpen}
        dialogTitle="Delete Post"
        dialogText="Are you sure you want to delete this post? This action cannot be undone."
        handleConfirm={handleDeleteConfirm}
        inProgress={performingAction}
        closeModal={() => setDeleteModalOpen(false)}
      />

      <CreatePostModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
        }}
        topics={postTopics}
        isEditing={true}
        postToEdit={post}
      />
    </Paper>
  )
}

const useStyles = makeStyles((theme) => ({
  postCard: {
    borderRadius: '12px',
    boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
    overflow: 'hidden'
  },
  postCardContent: {
    padding: theme.spacing(2.5, 3)
  },
  authorSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: theme.spacing(1.5),
    width: '100%'
  },
  postSection: {
    marginLeft: '4px'
  },
  authorInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  authorNameTime: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  postTime: {
    marginBottom: '2px'
  },
  authorName: {
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: 1.2,
    color: theme.palette.grey[700]
  },
  postMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  metadataDot: {
    fontSize: '10px',
    color: theme.palette.text.secondary,
    margin: '0 4px'
  },
  categoryChip: {
    height: '20px',
    fontSize: '12px',
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[600],
    borderRadius: '16px',
    fontWeight: 600
  },
  moreChip: {
    height: '20px',
    fontSize: '12px',
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[600],
    borderRadius: '16px',
    fontWeight: 600
    // color: theme.palette.text.secondary
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  flagIcon: {
    color: 'red'
  },
  pinIcon: {
    transform: 'rotate(45deg)',
    color: theme.palette.secondary.main
  },
  postTitle: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  postContent: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
    fontSize: '14px'
  },
  postStats: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  statsWrapper: {
    display: 'flex',
    gap: theme.spacing(3)
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  statCount: {
    fontSize: '0.875rem'
  },
  viewsCount: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  viewCountText: {
    fontSize: '0.875rem'
  },
  avatar: {
    width: 24,
    height: 24,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '13px',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
  },
  avatarImage: {
    borderRadius: '50%'
  },
  statusIndicators: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  pinItem: {
    backgroundColor: 'green'
  },
  flagItem: {
    backgroundColor: 'red'
  },
  statusItem: {
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '12px',
    padding: '2px 6px',
    marginLeft: theme.spacing(1),
    gap: theme.spacing(0.5)
  },
  pinIconInline: {
    transform: 'rotate(45deg)',
    fontSize: '14px'
  },
  flagIconInline: {
    fontSize: '14px'
  },
  statusLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.4px',
    color: 'white'
    // color: theme.palette.text.secondary
  },
  attachmentContainer: {
    marginBottom: theme.spacing(2)
  },
  attachmentImageCompressed: {
    maxHeight: 200,
    overflow: 'hidden',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  attachmentImageExpanded: {
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  attachmentImg: {
    width: '100%',
    maxHeight: 400,
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
    display: 'block'
  },
  pdfAttachment: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
    marginTop: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#f0f0f0'
    }
  }
}))

export default PostCard
